import React, { useEffect, useState } from 'react';
import { COASegment, AccountType } from './ChartOfAccounts';
import { AccountTypeBadge } from './AccountTypeBadge';
import { SaveIcon, RefreshCwIcon, GlobeIcon, BuildingIcon } from 'lucide-react';
interface OrgUnit {
  id: string;
  code: string;
  name: string;
  path: string;
}
interface SegmentEditorProps {
  segment: COASegment | null;
  accountTypes: AccountType[];
  isNew: boolean;
  onSave: (segment: COASegment) => void;
  onCancel: () => void;
  locale: string;
  availableOrgUnits?: OrgUnit[];
}
export function SegmentEditor({
  segment,
  accountTypes,
  isNew,
  onSave,
  onCancel,
  locale,
  availableOrgUnits = []
}: SegmentEditorProps) {
  const [formData, setFormData] = useState<COASegment>({
    id: '',
    segmentCode: '',
    segmentName: '',
    accountTypeId: '',
    isActive: true,
    activeFrom: new Date().toISOString().split('T')[0],
    fullGLPath: '',
    level: 1,
    scopeType: 'global',
    applicableOrgUnitIds: []
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  // Initialize form data when segment changes
  useEffect(() => {
    if (segment) {
      setFormData({
        ...segment,
        activeFrom: segment.activeFrom || new Date().toISOString().split('T')[0],
        scopeType: segment.scopeType || 'global',
        applicableOrgUnitIds: segment.applicableOrgUnitIds || []
      });
    } else {
      // Default values for new segment
      setFormData({
        id: '',
        segmentCode: '',
        segmentName: '',
        accountTypeId: accountTypes.length > 0 ? accountTypes[0].id : '',
        isActive: true,
        activeFrom: new Date().toISOString().split('T')[0],
        fullGLPath: '',
        level: 1,
        scopeType: 'global',
        applicableOrgUnitIds: []
      });
    }
  }, [segment, accountTypes]);
  // Handle form changes
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const {
      name,
      value,
      type
    } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    });
    // Clear error for this field if it exists
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  };
  // Handle scope type change
  const handleScopeTypeChange = (scopeType: 'global' | 'specific') => {
    setFormData({
      ...formData,
      scopeType,
      applicableOrgUnitIds: scopeType === 'global' ? [] : formData.applicableOrgUnitIds
    });
  };
  // Handle OrgUnit selection
  const handleOrgUnitToggle = (orgUnitId: string) => {
    const currentIds = formData.applicableOrgUnitIds || [];
    const newIds = currentIds.includes(orgUnitId) ? currentIds.filter(id => id !== orgUnitId) : [...currentIds, orgUnitId];
    setFormData({
      ...formData,
      applicableOrgUnitIds: newIds
    });
  };
  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Validate form
    const newErrors: Record<string, string> = {};
    if (!formData.segmentCode.trim()) {
      newErrors.segmentCode = 'Segment Code is required';
    }
    if (!formData.segmentName.trim()) {
      newErrors.segmentName = 'Segment Name is required';
    }
    if (!formData.accountTypeId) {
      newErrors.accountTypeId = 'Account Type is required';
    }
    if (formData.activeTo && formData.activeFrom && new Date(formData.activeTo) < new Date(formData.activeFrom)) {
      newErrors.activeTo = 'Active To date must be after Active From date';
    }
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    // Calculate fullGLPath if it's a new segment
    let fullGLPath = formData.segmentCode;
    if (formData.parentId && segment?.parentId) {
      // For child segments, we need to construct the path
      const parentPath = segment.fullGLPath.split('.').slice(0, -1).join('.');
      fullGLPath = parentPath ? `${parentPath}.${formData.segmentCode}` : formData.segmentCode;
    }
    onSave({
      ...formData,
      fullGLPath
    });
  };
  // Recalculate path
  const handleRecalculatePath = () => {
    // This is a simplified version - in a real app, this would call the backend
    // to recalculate the path based on the current parent-child relationships
    if (formData.parentId) {
      // For now, just update the local state to show the concept
      const parentSegment = segment?.fullGLPath.split('.').slice(0, -1).join('.');
      const newPath = parentSegment ? `${parentSegment}.${formData.segmentCode}` : formData.segmentCode;
      setFormData({
        ...formData,
        fullGLPath: newPath
      });
    } else {
      setFormData({
        ...formData,
        fullGLPath: formData.segmentCode
      });
    }
  };
  return <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-12012">
      <div className="px-4 py-5 sm:px-6 border-b border-gray-200" data-id="element-12013">
        <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-12014">
          {isNew ? 'Create New Segment' : 'Edit Segment'}
        </h3>
        <p className="mt-1 max-w-2xl text-sm text-gray-500" data-id="element-12015">
          {isNew ? 'Enter details for the new segment' : 'Update segment details'}
        </p>
      </div>
      <div className="px-4 py-5 sm:p-6" data-id="element-12016">
        <form onSubmit={handleSubmit} data-id="element-12017">
          <div className="space-y-6" data-id="element-12018">
            {/* Segment Code */}
            <div data-id="element-12019">
              <label htmlFor="segmentCode" className="block text-sm font-medium text-gray-700" data-id="element-12020">
                Segment Code *
              </label>
              <div className="mt-1" data-id="element-12021">
                <input type="text" name="segmentCode" id="segmentCode" value={formData.segmentCode} onChange={handleChange} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md ${errors.segmentCode ? 'border-red-300' : ''}`} data-id="element-12022" />
                {errors.segmentCode && <p className="mt-1 text-sm text-red-600" data-id="element-12023">
                    {errors.segmentCode}
                  </p>}
              </div>
            </div>
            {/* Segment Name */}
            <div data-id="element-12024">
              <label htmlFor="segmentName" className="block text-sm font-medium text-gray-700" data-id="element-12025">
                Segment Name *
              </label>
              <div className="mt-1" data-id="element-12026">
                <input type="text" name="segmentName" id="segmentName" value={formData.segmentName} onChange={handleChange} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md ${errors.segmentName ? 'border-red-300' : ''}`} data-id="element-12027" />
                {errors.segmentName && <p className="mt-1 text-sm text-red-600" data-id="element-12028">
                    {errors.segmentName}
                  </p>}
              </div>
            </div>
            {/* Account Type */}
            <div data-id="element-12029">
              <label htmlFor="accountTypeId" className="block text-sm font-medium text-gray-700" data-id="element-12030">
                Account Type *
              </label>
              <div className="mt-1" data-id="element-12031">
                <select id="accountTypeId" name="accountTypeId" value={formData.accountTypeId} onChange={handleChange} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md ${errors.accountTypeId ? 'border-red-300' : ''}`} data-id="element-12032">
                  <option value="" data-id="element-12033">Select an account type</option>
                  {accountTypes.map(type => <option key={type.id} value={type.id} data-id="element-12034">
                      {type.name}
                    </option>)}
                </select>
                {errors.accountTypeId && <p className="mt-1 text-sm text-red-600" data-id="element-12035">
                    {errors.accountTypeId}
                  </p>}
              </div>
              {formData.accountTypeId && <div className="mt-2" data-id="element-12036">
                  <AccountTypeBadge type={accountTypes.find(at => at.id === formData.accountTypeId)!} size="md" data-id="element-12037" />
                </div>}
            </div>
            {/* Parent Segment (read-only) */}
            {formData.parentId && <div data-id="element-12038">
                <label className="block text-sm font-medium text-gray-700" data-id="element-12039">
                  Parent Segment
                </label>
                <div className="mt-1" data-id="element-12040">
                  <input type="text" value={segment?.parentId ? `${segment.fullGLPath.split('.').slice(0, -1).join('.')}` : ''} readOnly className="bg-gray-50 shadow-sm block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-12041" />
                </div>
              </div>}
            {/* Full GL Path */}
            <div data-id="element-12042">
              <div className="flex justify-between items-center" data-id="element-12043">
                <label className="block text-sm font-medium text-gray-700" data-id="element-12044">
                  Full GL Path
                </label>
                <button type="button" onClick={handleRecalculatePath} className="inline-flex items-center px-2.5 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-12045">
                  <RefreshCwIcon className="h-3 w-3 mr-1" data-id="element-12046" />
                  Recalculate
                </button>
              </div>
              <div className="mt-1" data-id="element-12047">
                <input type="text" value={formData.fullGLPath} readOnly className="bg-gray-50 shadow-sm block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-12048" />
              </div>
            </div>
            {/* Active Status */}
            <div className="flex items-center" data-id="element-12049">
              <input id="isActive" name="isActive" type="checkbox" checked={formData.isActive} onChange={handleChange} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" data-id="element-12050" />
              <label htmlFor="isActive" className="ml-2 block text-sm text-gray-700" data-id="element-12051">
                Active
              </label>
            </div>
            {/* Active From */}
            <div data-id="element-12052">
              <label htmlFor="activeFrom" className="block text-sm font-medium text-gray-700" data-id="element-12053">
                Active From
              </label>
              <div className="mt-1" data-id="element-12054">
                <input type="date" name="activeFrom" id="activeFrom" value={formData.activeFrom} onChange={handleChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-12055" />
              </div>
            </div>
            {/* Active To */}
            <div data-id="element-12056">
              <label htmlFor="activeTo" className="block text-sm font-medium text-gray-700" data-id="element-12057">
                Active To
              </label>
              <div className="mt-1" data-id="element-12058">
                <input type="date" name="activeTo" id="activeTo" value={formData.activeTo || ''} onChange={handleChange} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md ${errors.activeTo ? 'border-red-300' : ''}`} data-id="element-12059" />
                {errors.activeTo && <p className="mt-1 text-sm text-red-600" data-id="element-12060">{errors.activeTo}</p>}
              </div>
            </div>
            {/* Org Unit (optional) */}
            <div data-id="element-12061">
              <label htmlFor="orgUnitId" className="block text-sm font-medium text-gray-700" data-id="element-12062">
                Org Unit (optional)
              </label>
              <div className="mt-1" data-id="element-12063">
                <select id="orgUnitId" name="orgUnitId" value={formData.orgUnitId || ''} onChange={handleChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-12064">
                  <option value="" data-id="element-12065">None</option>
                  <option value="org-001" data-id="element-12066">Corporate</option>
                  <option value="org-002" data-id="element-12067">North America</option>
                  <option value="org-003" data-id="element-12068">Europe</option>
                  <option value="org-004" data-id="element-12069">Asia Pacific</option>
                </select>
              </div>
            </div>

            {/* OrgUnit Scope Configuration */}
            <div className="border-t border-gray-200 pt-6" data-id="element-12070">
              <h4 className="text-sm font-medium text-gray-900 mb-4" data-id="element-12071">
                Organisation Unit Scope
              </h4>
              <p className="text-sm text-gray-500 mb-4" data-id="element-12072">
                Configure which organisation units can use this GL code segment
              </p>

              {/* Scope Type Selection */}
              <div className="space-y-3" data-id="element-12073">
                <div className="flex items-start" data-id="element-12074">
                  <div className="flex items-center h-5" data-id="element-12075">
                    <input id="scope-global" name="scopeType" type="radio" checked={formData.scopeType === 'global'} onChange={() => handleScopeTypeChange('global')} className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300" data-id="element-12076" />
                  </div>
                  <div className="ml-3" data-id="element-12077">
                    <label htmlFor="scope-global" className="font-medium text-gray-700 flex items-center" data-id="element-12078">
                      <GlobeIcon className="h-4 w-4 mr-2 text-green-600" data-id="element-12079" />
                      Global (All Organisation Units)
                    </label>
                    <p className="text-sm text-gray-500" data-id="element-12080">
                      This GL code can be used by all organisation units
                    </p>
                  </div>
                </div>

                <div className="flex items-start" data-id="element-12081">
                  <div className="flex items-center h-5" data-id="element-12082">
                    <input id="scope-specific" name="scopeType" type="radio" checked={formData.scopeType === 'specific'} onChange={() => handleScopeTypeChange('specific')} className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300" data-id="element-12083" />
                  </div>
                  <div className="ml-3 flex-1" data-id="element-12084">
                    <label htmlFor="scope-specific" className="font-medium text-gray-700 flex items-center" data-id="element-12085">
                      <BuildingIcon className="h-4 w-4 mr-2 text-orange-600" data-id="element-12086" />
                      Specific Organisation Units
                    </label>
                    <p className="text-sm text-gray-500 mb-3" data-id="element-12087">
                      Select which organisation units can use this GL code
                    </p>

                    {/* OrgUnit Selection */}
                    {formData.scopeType === 'specific' && <div className="mt-3 space-y-2 max-h-48 overflow-y-auto border border-gray-200 rounded-md p-3 bg-gray-50" data-id="element-12088">
                        {availableOrgUnits.length === 0 ? <p className="text-sm text-gray-500 italic" data-id="element-12089">
                            No organisation units available
                          </p> : availableOrgUnits.map(orgUnit => <div key={orgUnit.id} className="flex items-start" data-id="element-12090">
                              <div className="flex items-center h-5" data-id="element-12091">
                                <input id={`orgunit-${orgUnit.id}`} type="checkbox" checked={formData.applicableOrgUnitIds?.includes(orgUnit.id) || false} onChange={() => handleOrgUnitToggle(orgUnit.id)} className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded" data-id="element-12092" />
                              </div>
                              <div className="ml-3" data-id="element-12093">
                                <label htmlFor={`orgunit-${orgUnit.id}`} className="text-sm font-medium text-gray-700" data-id="element-12094">
                                  {orgUnit.name}
                                </label>
                                <p className="text-xs text-gray-500" data-id="element-12095">
                                  {orgUnit.path}
                                </p>
                              </div>
                            </div>)}
                      </div>}

                    {formData.scopeType === 'specific' && formData.applicableOrgUnitIds && formData.applicableOrgUnitIds.length > 0 && <div className="mt-2 text-sm text-gray-600" data-id="element-12096">
                          <span className="font-medium" data-id="element-12097">
                            {formData.applicableOrgUnitIds.length}
                          </span>{' '}
                          organisation unit(s) selected
                        </div>}
                  </div>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end space-x-3 pt-4" data-id="element-12098">
              <button type="button" onClick={onCancel} className="px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-12099">
                Cancel
              </button>
              <button type="submit" className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-12100">
                <SaveIcon className="h-4 w-4 mr-2" data-id="element-12101" />
                {isNew ? 'Create Segment' : 'Save Changes'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>;
}